import Autocomplete from "@mui/material/Autocomplete";
import { useTheme } from "@mui/material/styles";
import { ILookupItemModel, ILookupItemSimpleModel } from "../types/types";
import React from "react";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import Stack from "@mui/material/Stack";

interface IMultiValueAutoCompleteCategoryProps {
  options: ILookupItemSimpleModel[];
  onChange: (
    event: React.ChangeEvent<{}>,
    value: ILookupItemSimpleModel[] | null
  ) => void;
  helperText: any;
  error: any;
  value: ILookupItemSimpleModel[] | null;
  label: string;
  placeholder: string;
  id: string;
  limit?: number;
  readOnly?: boolean;
  freeSolo?: boolean;
}

export default function MultiValueAutoCompleteCategory(
  props: IMultiValueAutoCompleteCategoryProps
) {
  const {
    onChange,
    value,
    label,
    placeholder,
    options,
    id,
    limit,
    readOnly = false,
    freeSolo = true,
  } = props;

  // The live theme, so the "add new" hint follows affiliate branding.
  const brand = useTheme();
  const [inputValue, setInputValue] = React.useState("");
  const getOptionsWithAddNew = React.useMemo(() => {
    if (!inputValue.trim()) {
      return options;
    }

    const searchTerm = inputValue.trim().toLowerCase();
    const hasExactMatch = options.some(
      (option) => option.name?.toLowerCase() === searchTerm
    );

    if (!hasExactMatch && inputValue.trim().length > 0) {
      // Add "Add [name]" option at the beginning
      const addNewOption: ILookupItemSimpleModel & { isAddNew?: boolean } = {
        id: -1, // Special ID to identify this as "add new" option
        name: `Add "${inputValue.trim()}"`,
        isAddNew: true,
      };
      return [addNewOption, ...options];
    }

    return options;
  }, [options, inputValue]);
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Enter" && inputValue.trim() && freeSolo) {
      event.preventDefault();
      const trimmedValue = inputValue.trim();

      // Check if the value already exists in options
      const existsInOptions = options.some(
        (option) => option.name?.toLowerCase() === trimmedValue.toLowerCase()
      );

      // Check if the value already exists in selected values
      const existsInSelected = value?.some(
        (item) => item.name?.toLowerCase() === trimmedValue.toLowerCase()
      );

      if (!existsInOptions && !existsInSelected) {
        const newItem: ILookupItemSimpleModel = {
          id: Math.floor(Math.random() * -1e12),
          name: trimmedValue,
        };

        const newValue = [...(value || []), newItem];
        setInputValue("");
        onChange(event as any, newValue);
      }
    }
  };
  return (
    <>
      <Grid container direction={"column"} gap={1}>
        <Autocomplete
          multiple
          id={id}
          readOnly={readOnly}
          openOnFocus
          ChipProps={{ variant: "outlined", color: "primary" }}
          limitTags={3}
          disableClearable
          selectOnFocus
          clearOnBlur={false}
          freeSolo={
            freeSolo && !(limit && value && value.length >= limit)

            // freeSolo && (limit && value && value.length >= limit ? false : true)
          }
          getOptionDisabled={(options) =>
            limit && value && value.length >= limit ? true : false
          }
          options={getOptionsWithAddNew}
          value={value || []}
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            // Clear input on blur or reset to prevent autoSelect issues
            if (reason === "reset" || reason === "clear") {
              setInputValue("");
            } else {
              setInputValue(newInputValue);
            }
          }}
          // isOptionEqualToValue={(option, value) => option.id === value.id}
          // filterOptions={(options) => options} // Disable default filtering
          // getOptionDisabled={(option) =>
          //   option.id !== 0 && !!value?.some((v) => v.id === option.id)
          // }
          // onChange={(event, newValue) => {
          //   const values = newValue.map((item) =>
          //     typeof item === "string"
          //       ? { id: Math.floor(Math.random() * -1e12), name: item }
          //       : item
          //   );
          //   onChange(event, values);
          // }}
          onChange={(
            event: any,
            newValue: (ILookupItemSimpleModel | string)[]
          ) => {
            const processedValues = newValue.map((item) => {
              if (typeof item === "string") {
                return { id: Math.floor(Math.random() * -1e12), name: item };
              } else if ((item as any).isAddNew) {
                // Handle "Add new" option - extract the actual name from "Add [name]"
                const actualName = inputValue.trim();
                return {
                  id: Math.floor(Math.random() * -1e12),
                  name: actualName,
                };
              } else {
                return item;
              }
            });
            // Clear input after selection
            setInputValue("");
            onChange!(event, processedValues);
          }}
          getOptionLabel={(option) => {
            if (!option) return "";
            return typeof option === "string" ? option : option.name!;
          }}
          // getOptionKey={(option) => {
          //   if (typeof option === "string") {
          //     return option;
          //   }
          //   if (option.id) {
          //     return option.id;
          //   }
          //   return option.id!;
          // }}
          getOptionKey={(option) => {
            if (typeof option === "string") {
              return option;
            }
            if (option.id) {
              return option.id;
            }
            return option.id!;
          }}
          filterOptions={(x) => x}
          // renderOption={(props, option) => <li {...props}>{option.name}</li>}

          //         renderOption={(props, option) => {
          //   const isAddNew = (option as any).isAddNew;
          //   return (
          //     <li
          //       {...props}
          //       style={{
          //         ...props.style,
          //         fontWeight: isAddNew ? "bold" : "normal",
          //         color: isAddNew ? "#1976d2" : "inherit",
          //         fontStyle: isAddNew ? "italic" : "normal",
          //       }}
          //     >
          //       {typeof option === "string" ? option : option.name}
          //     </li>
          //   );
          // }}
          renderOption={(props, option) => {
            const currentOption = option as ILookupItemSimpleModel;
            const isAddNew = (option as any).isAddNew;
            return (
              <li
                {...props}
                key={currentOption.id}
                style={{
                  ...props.style,
                  fontWeight: isAddNew ? "bold" : "normal",
                  color: isAddNew ? brand.palette.primary.dark : "inherit",
                  fontStyle: isAddNew ? "italic" : "normal",
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%"
                >
                  <span>{currentOption.name}</span>
                </Stack>
              </li>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              variant="outlined"
              onKeyDown={handleKeyDown}
              InputLabelProps={{
                shrink: true,
                sx: {
                  // position: "relative",
                  // transform: "none",
                  // marginBottom: "8px",
                },
              }}
              inputProps={{
                ...params.inputProps,
                maxLength: 50,
                style: {
                  // paddingTop: 0,
                },
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  // marginTop: "28px",
                },
              }}
              label={label}
              placeholder={placeholder}
            />
          )}
        />
      </Grid>
    </>
  );
}
